import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { createModuleStrings } from '@platform/core/lib/moduleStrings';
import { PageCrumbs } from '@platform/engine/list/header/PageCrumbs';
import { useAppSelector } from '@platform/state/hooks';
import { selectLang } from '@platform/state/selectors';
import { Spinner } from '@platform/ui';
import type { ISettingsLoadingPageProps } from './ISettingsLoadingPageProps';
import { useCustomizeHead } from './useCustomizeHead';

/** Нерозривний пробіл: рядок шапки тримає висоту, доки тексту ще немає (без стрибка вмісту). */
const HOLD = '\u00a0';

/**
 * Сторінка модуля в «Налаштуваннях», доки вантажаться пакет модуля чи його визначення (рішення 3 етапу 11: сторінка ніколи не порожня):
 * крихти «Простір › Налаштування › Форми» і повернення «← Форми» / «← Модулі» — одразу; у редакторі форми — і заголовок «Форма «Видаток»»
 * (з крихтою), щойно сервер віддав визначення: ті самі підписи, що покаже готова сторінка (підписи визначення сервера, без рядків пакета,
 * які ще не завантажено); рядок підзаголовка тримає місце; під шапкою — індикатор завантаження. Ті самі класи, що в готовій шапці.
 */
export function SettingsLoadingPage({ ws, moduleKey, section, global, definition }: ISettingsLoadingPageProps) {
  const lang = useAppSelector(selectLang);
  const strings = useMemo(() => createModuleStrings(moduleKey, lang, () => undefined), [moduleKey, lang]);
  // Визначення з файлів пакета має лише англійські підписи (переклад — у рядках пакета, що ще вантажаться), тому назва — лише з сервера.
  const head = useCustomizeHead(ws, moduleKey, section, global, definition.source === 'server' ? definition.data : undefined, strings);
  const title = head.forms ? head.title : '';
  const crumbs = title ? head.crumbs : head.crumbs.slice(0, -1);
  const back = <Link className="st-back" to={head.links.backTo}>{head.links.backLabel}</Link>;
  return (
    <div className="lv-page" aria-busy="true">
      <PageCrumbs items={crumbs} />
      <div className={head.forms ? 'st-bp st-fpage' : 'st-bp st-rpage'}>
        {head.forms ? (
          <div className="st-fhead">
            {back}
            <div className="st-head__row st-head__row--end">
              <div className="st-fhead__text">
                <h1 className="st-head__title" aria-hidden={title ? undefined : true}>{title || HOLD}</h1>
                <div className="st-head__sub" aria-hidden="true">{HOLD}</div>
              </div>
            </div>
          </div>
        ) : back}
        <Spinner />
      </div>
    </div>
  );
}
